import type { Hit } from '@algolia/client-search';
import createDebug from 'debug';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { ctApi } from '@/api';
import {
  AlgoliaCategory,
  CategoryQueryOptions,
  entryCountsForChildCategories,
  getChildCategories,
  SynthesizedAlgoliaCategory,
} from '@/api/algolia';
import { categoryQuery } from '@/graphql/categoryQuery';
import { BuiltCategory } from '@/utils/category';

const debug = createDebug('nuts:store:category');

const LOCALE = 'en';

const defaultCategory = {
  ancestors: [],
  assets: [],
  childCount: 0,
  description: '',
  hidden: false,
  id: '',
  key: '',
  name: '',
  orderHint: 0,
  path: '',
  stagedProductCount: 0,
  urlName: '',
  version: 0,
};

export const useCategory = defineStore('category', () => {
  const category = ref<BuiltCategory>(defaultCategory);
  const subcategories = ref<Hit<AlgoliaCategory | SynthesizedAlgoliaCategory>[]>([]);
  const productsWithCategory = ref<
    {
      name: string;
      key: string;
      sku: string;
      path: string;
      image: string;
    }[]
  >([]);
  const entryCountsPerSubcategory = ref<Record<string, number>>({});
  const updating = ref(false);

  const clearCategory = () => {
    category.value = defaultCategory;
  };

  interface LoadCategoryOptions {
    forceRefresh?: boolean;
    cleanHtml?: boolean;
  }
  const loadCategory = async (
    key?: string,
    { cleanHtml = true, forceRefresh = false }: LoadCategoryOptions = {},
  ) => {
    if (!key) {
      debug('no category key given');
      if (forceRefresh) {
        debug('unsetting category info due to `forceRefresh` flag');
        clearCategory();
      }
      return undefined;
    }

    if (category.value.key === key && !forceRefresh) {
      debug('category already loaded');
      return undefined;
    }

    updating.value = true;

    const {
      body: { data },
    } = await ctApi
      .graphql()
      .post({
        body: {
          query: categoryQuery,
          variables: {
            key,
            locale: LOCALE,
          },
        },
      })
      .execute();
    debug('received category data');
    const builtCategory = await BuiltCategory.fromCT(data.category, {
      cleanHtml,
      locale: LOCALE,
    });

    category.value = builtCategory;
    updating.value = false;

    return builtCategory;
  };

  const loadSubcategories = async (options: CategoryQueryOptions) => {
    const childCategories = await getChildCategories(options);

    subcategories.value = childCategories.hits;

    return subcategories;
  };

  const loadEntryCountsPerSubcategory = async (options: CategoryQueryOptions) => {
    const entryCounts = await entryCountsForChildCategories(options);

    entryCountsPerSubcategory.value = entryCounts;

    return entryCounts;
  };

  return {
    category,
    clearCategory,
    entryCountsPerSubcategory,
    loadCategory,
    loadEntryCountsPerSubcategory,
    loadSubcategories,
    productsWithCategory,
    subcategories,
    updating,
  };
});
